import { Box, InputLabel, Typography, makeStyles } from "@material-ui/core";
import { useState } from "react";
import { GroupBase, OptionsOrGroups } from "react-select5";
import ReactCreatableSelect, { CreatableProps } from "react-select5/creatable";
import { GroupComponent } from "./components/group";
import { GroupHeadingComponent } from "./components/group-heading";
import { MultiValueRemoveComponent } from "./components/multi-value-remove";
import { OptionComponent } from "./components/option";
import { useCreateableSelectStyle } from "./hooks/use-creatable-select-style";

type SelectProps<
  Option,
  Group extends GroupBase<Option> = GroupBase<Option>
> = CreatableProps<Option, true, Group> & {
  inputLabel?: string;
  addNewLabel?: string;
  styleProps?: Parameters<typeof useCreateableSelectStyle<Option, true, Group>>["1"];
};

export function MultiCreatableGroupedSelect<
  Option extends object,
  Group extends GroupBase<Option> = GroupBase<Option>
>(mainProps: SelectProps<Option, Group>) {
  const { creatableSelectStyle, creatableSelectTheme } = useCreateableSelectStyle<
    Option,
    true,
    Group
  >({ grouped: true }, mainProps.styleProps);
  const classes = useStyles();

  const [value, setValue] = useState<string>();

  const { inputLabel, addNewLabel } = mainProps;

  const addNewOption = [
    { label: addNewLabel, options: [{ value: value, label: value }] }
  ];

  const options = [
    ...(mainProps.options ?? []),
    ...(value ? addNewOption : [])
  ] as OptionsOrGroups<Option, Group>;

  return (
    <Box className={classes.root}>
      {inputLabel && (
        <InputLabel>
          <Typography variant="body1" style={{ opacity: mainProps.isDisabled ? 0.5 : 1 }}>
            {inputLabel}
          </Typography>
        </InputLabel>
      )}
      <ReactCreatableSelect<Option, true, Group>
        placeholder=""
        {...mainProps}
        options={options}
        components={{
          Option: (p) => OptionComponent(p),
          Group: (p) =>
            GroupComponent({ ...p, addNewLabel, value, mainOptions: mainProps.options }),
          GroupHeading: (p) =>
            GroupHeadingComponent({
              ...p,
              textVariant: "subtitle1"
            }),
          MultiValueRemove: (p) => MultiValueRemoveComponent(p),
          DropdownIndicator: null
        }}
        inputValue={value}
        onInputChange={(value) => {
          setValue(value);
        }}
        getOptionValue={(option) => {
          return "value" in option && typeof option.value === "string"
            ? option.value.toLowerCase()
            : JSON.stringify(option).toLowerCase();
        }}
        formatCreateLabel={() => undefined}
        styles={creatableSelectStyle}
        theme={(theme) => ({
          ...theme,
          ...creatableSelectTheme
        })}
        backspaceRemovesValue={false}
      />
    </Box>
  );
}

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    rowGap: "0.5rem"
  }
}));

export default MultiCreatableGroupedSelect;
